import { computed, onScopeDispose, shallowRef, watch, type Ref } from "vue";
import type { FunctionReference } from "convex/server";
import { getProjectsConvexClient, recoverProjectsConvexSubscription } from "../convexClient";
import { captureConvexError } from "../telemetry";

/** App-owned subscription with the same session recovery used by project chat. */
export function useChatQuery<Q extends FunctionReference<"query">>(fn: Q, args: Ref<Q["_args"] | null>) {
  const data = shallowRef<Q["_returnType"]>();
  const error = shallowRef<Error | null>(null);
  let unsubscribe: (() => void) | undefined;
  let generation = 0;
  function stopSubscription() {
    const stop = unsubscribe;
    unsubscribe = undefined;
    stop?.();
  }
  function subscribe(reset = true, attempt = 0) {
    const current = ++generation;
    stopSubscription();
    if (reset) data.value = undefined;
    error.value = null;
    const input = args.value;
    if (!input) return;
    unsubscribe = getProjectsConvexClient().onUpdate(fn, input, value => {
      if (current === generation) { data.value = value; error.value = null; }
    }, cause => {
      if (current !== generation) return;
      void recoverProjectsConvexSubscription({ operation: "chat.query", error: cause, recoveryAttempt: attempt })
        .then(result => {
          if (current !== generation) return;
          if (result.didRecover) subscribe(false, result.nextRecoveryAttempt);
          else {
            error.value = cause;
            captureConvexError(cause, { operation: "chat.query_failed", level: "error" });
          }
        });
    });
  }
  watch(args, () => subscribe(), { immediate: true });
  onScopeDispose(() => { generation++; stopSubscription(); });
  return { data, error, isLoading: computed(() => !!args.value && data.value === undefined && !error.value), refresh: () => subscribe() };
}
